import { Box, styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { animationTransition } from '@shared/utils/animationTransition';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';

// body2 is 16px at 1.5 line-height -> 24px a line. Two lines is the collapsed height.
export const COLLAPSED_NOTE_HEIGHT = 48;
export const EXPANDED_NOTE_MAX_HEIGHT = 240;
const NOTE_PADDING_Y = 8;
const NOTE_PADDING_X = 12;
// Toolbar buttons plus joykit's padding.
const TOOLBAR_OPEN_MAX_HEIGHT = 56;

// `-webkit-line-clamp` paints the ellipsis but does not cap the height, so a long note would
// still reserve its full height and push the Hotels section down (JW-70).
export const ClampedNotePreview = styled(TextEllipsisWrapper)`
  max-height: ${pxToRem(COLLAPSED_NOTE_HEIGHT)};
`;

/**
 * Collapsed vs expanded rides on `data-focused`, which the editor root already sets. It cannot be
 * a prop: joykit freezes the Jodit config at mount. The rings stay `StyledRichTextEditor`'s.
 */
export const PageNoteEditorWrapper = styled(Box)`
  /* The editor's global style puts paragraphs at body3 with browser margins — a page-body size.
     Pin it to the resting box's body2 so focusing the field does not reflow the text. */
  & .jodit-editor p,
  & span.jodit-placeholder {
    ${({ theme }) => theme.typography.variants.body2};
  }
  & .jodit-editor p {
    margin: 0;
  }
  & .jodit-editor p + p {
    margin-top: ${({ theme }) => theme.space[4]};
  }
  & span.jodit-placeholder {
    color: ${({ theme }) => theme.colors.mono8};
  }

  /* joykit sizes this for full-page use. Match the resting box, in both states. */
  & div.jodit .jodit-workplace .jodit-wysiwyg {
    padding: ${pxToRem(NOTE_PADDING_Y)} ${pxToRem(NOTE_PADDING_X)};
    transition: max-height 150ms ease-out;
  }

  /* The toolbar collapses rather than unmounts: display:none snaps the height. */
  & div.jodit-toolbar__box {
    overflow: hidden;
    /* max-height cannot animate to or from none; overshooting is fine. */
    max-height: ${pxToRem(TOOLBAR_OPEN_MAX_HEIGHT)};
    transition:
      max-height 150ms ease-out,
      opacity 150ms ease-out;
  }

  & .jodit-editor:not([data-focused='true']) {
    div.jodit-toolbar__box {
      max-height: 0;
      opacity: 0;
      padding: 0;
      border-bottom: none;
    }
    div.jodit .jodit-workplace .jodit-wysiwyg {
      max-height: ${pxToRem(COLLAPSED_NOTE_HEIGHT + NOTE_PADDING_Y * 2)};
      overflow-y: auto;
    }
  }
`;

/**
 * The resting two-line box. It stands in for the editor whenever the field is not being edited,
 * so it carries the same field-contract rings, painted as an `::after` like the editor's.
 */
export const CollapsedNoteBox = styled(Box)`
  position: relative;
  padding: ${pxToRem(NOTE_PADDING_Y)} ${pxToRem(NOTE_PADDING_X)};
  transition: ${animationTransition('border-color', 'box-shadow')};

  &::after {
    content: ' ';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${({ theme }) => theme.radii.field};
    box-shadow: 0 0 0 2px transparent;
    pointer-events: none;
    transition: ${animationTransition('box-shadow')};
  }

  &:hover::after {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.mono6};
  }

  /* After :hover, at equal specificity, so focus wins while the pointer rests on it. */
  &:focus-visible {
    outline: none;
  }
  &:focus-visible::after {
    box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.inputBorderFocused};
  }

  &:disabled:hover::after {
    box-shadow: 0 0 0 2px transparent;
  }
`;
